/**
 * Phase 5 (2026-05-25) — Canonical hover-help primitive.
 *
 * Supersedes:
 *   - src/components/ui/Tooltip.tsx (Phase F / C-3, pale-yellow generic)
 *   - src/components/specialization-lab/LabTooltip.tsx (Phase L.16, XP Luna)
 *
 * Why one component instead of two: the two tooltip systems had drifted
 * into different visuals + different a11y wiring. HelpTip combines
 * Tooltip's portal positioning + collision flip + child cloning with
 * LabTooltip's XP Luna yellow style + aria-describedby linkage +
 * keyed lookup. It also adds: long-press for touch, Escape and
 * click-outside dismissal, a two-tier short/long body with an inline
 * "Learn more" expander, and registry lookup by HelpCopyKey.
 *
 * Three modes of use:
 *
 *   // 1. Keyed (preferred — resolves to the centralized helpCopy registry):
 *   <HelpTip k="explorer.toolbar.up" wrap>
 *     <button onClick={onUp}>Up</button>
 *   </HelpTip>
 *
 *   // 2. Ad-hoc strings (escape hatch — same shape as the registry entry):
 *   <HelpTip short="Open Document Library" wrap>
 *     <button>Documents</button>
 *   </HelpTip>
 *
 *   // 3. Standalone (?) chip with no wrapped child:
 *   <HelpTip k="…" />
 *
 * `wrap` means: clone the single child and inject hover/focus handlers
 * onto it directly (no extra DOM). Without `wrap` HelpTip renders its
 * own small "(?)" chip element.
 *
 * Long-form bodies: if the entry has a `long` field (string or
 * ReactNode), the bubble shows a "→ Learn more" chevron. Clicking
 * expands the bubble inline (still in the same portal) so the user
 * never leaves the surface.
 *
 * Touch: on `pointerType === 'touch'`, a long-press (~500 ms) opens
 * the bubble; a regular tap fires the wrapped child's click handler
 * normally. A tap anywhere outside the bubble (after it's shown)
 * dismisses it.
 */
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { TOOLTIP_DELAY_MS } from '../../lib/ui/uiTimingConfig';
import { getHelpCopy, type HelpCopyKey } from '../../lib/help/copy';

const FONT = 'var(--xp-font-body)';
const LONG_PRESS_MS = 500;
const MIN_BUBBLE_WIDTH = 160;
const MAX_BUBBLE_WIDTH = 320;
const VERTICAL_GAP = 6;

/**
 * Shared shape of every helpCopy registry entry. `verified` is required
 * for every keyed entry in Phase 5; it powers the truth-contract test
 * in `src/lib/help/__tests__/copyTruth.test.ts`.
 */
export interface HelpCopyEntry {
  short: string;
  long?: ReactNode | string;
  verified: {
    component: string;
    handler: string;
    triggers: ReadonlyArray<string>;
  };
}

interface CommonProps {
  /**
   * Optional placement of the bubble relative to the trigger. Defaults
   * to 'top' (flips to 'bottom' on viewport collision).
   */
  placement?: 'top' | 'bottom';
  /**
   * Override the shared TOOLTIP_DELAY_MS. Tests pass a smaller value
   * to avoid fake-timer overhead; product callers should leave this
   * unset so the single config source wins.
   */
  delayMs?: number;
  /**
   * Stable test id for the rendered bubble. Defaults to a string derived
   * from the help key, e.g. `tooltip-explorer-toolbar-up`.
   */
  testId?: string;
}

interface KeyedProps extends CommonProps {
  k: HelpCopyKey;
  short?: never;
  long?: never;
  wrap?: boolean;
  children?: ReactElement;
}

interface AdHocProps extends CommonProps {
  k?: never;
  short: string;
  long?: ReactNode | string;
  wrap?: boolean;
  children?: ReactElement;
}

export type HelpTipProps = KeyedProps | AdHocProps;

interface BubblePosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

function resolveCopy(
  props: HelpTipProps,
): { short: string; long: ReactNode | string | undefined; testId: string } {
  if ('k' in props && props.k !== undefined) {
    const entry = getHelpCopy(String(props.k));
    return {
      short: entry?.short ?? `MISSING HELP COPY: ${String(props.k)}`,
      long: entry?.long,
      testId: props.testId ?? `tooltip-${String(props.k).replace(/\./g, '-')}`,
    };
  }
  // AdHocProps narrowing: short is required, long optional.
  const adhoc = props as AdHocProps;
  return {
    short: adhoc.short,
    long: adhoc.long,
    testId: adhoc.testId ?? 'tooltip-adhoc',
  };
}

function computePosition(
  rect: DOMRect,
  bubbleWidth: number,
  preferred: 'top' | 'bottom',
  viewportHeight: number,
): BubblePosition {
  const wantTop = preferred === 'top';
  const spaceAbove = rect.top;
  const spaceBelow = viewportHeight - rect.bottom;
  const useTop = wantTop ? spaceAbove > 60 : spaceBelow < 60;
  const placement: 'top' | 'bottom' = useTop ? 'top' : 'bottom';
  const top =
    placement === 'top' ? rect.top - VERTICAL_GAP : rect.bottom + VERTICAL_GAP;
  const left = Math.max(
    4,
    Math.min(
      window.innerWidth - bubbleWidth - 4,
      rect.left + rect.width / 2 - bubbleWidth / 2,
    ),
  );
  return { top, left, placement };
}

export function HelpTip(props: HelpTipProps): ReactElement {
  const { placement = 'top', delayMs = TOOLTIP_DELAY_MS } = props;
  const { short, testId } = resolveCopy(props);
  const wrap = (props as { wrap?: boolean }).wrap === true && isValidElement(props.children);

  const bubbleId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);

  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<BubblePosition | null>(null);

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const showNow = useCallback(() => {
    const el = triggerRef.current;
    if (el === null) return;
    const rect = el.getBoundingClientRect();
    const widthEstimate = Math.min(
      MAX_BUBBLE_WIDTH,
      Math.max(MIN_BUBBLE_WIDTH, short.length * 7),
    );
    setPos(computePosition(rect, widthEstimate, placement, window.innerHeight));
    setVisible(true);
  }, [short, placement]);

  const showAfterDelay = useCallback(() => {
    clearShowTimer();
    showTimerRef.current = window.setTimeout(() => {
      showNow();
    }, delayMs);
  }, [showNow, delayMs, clearShowTimer]);

  const hideNow = useCallback(() => {
    clearShowTimer();
    clearLongPressTimer();
    setVisible(false);
  }, [clearShowTimer, clearLongPressTimer]);

  useEffect(() => {
    return () => {
      clearShowTimer();
      clearLongPressTimer();
    };
  }, [clearShowTimer, clearLongPressTimer]);

  // Escape + click-outside dismissal while visible.
  useEffect(() => {
    if (!visible) return;
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        hideNow();
      }
    }
    function onPointerDown(e: globalThis.PointerEvent) {
      const target = e.target as Node | null;
      if (target === null) return;
      if (bubbleRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      hideNow();
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [visible, hideNow]);

  // Shared trigger event wiring used by both wrap=true and chip modes.
  const triggerHandlers = {
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType === 'touch') return; // touch handled via long-press
      showAfterDelay();
    },
    onPointerLeave: (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      hideNow();
    },
    onFocus: (_e: FocusEvent) => showAfterDelay(),
    onBlur: (_e: FocusEvent) => hideNow(),
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      clearLongPressTimer();
      longPressTimerRef.current = window.setTimeout(() => {
        showNow();
      }, LONG_PRESS_MS);
    },
    onPointerUp: (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      // If the long-press timer hasn't fired yet, it was a tap — cancel
      // so the normal click handler runs.
      clearLongPressTimer();
    },
    onPointerCancel: (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      clearLongPressTimer();
    },
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Escape') hideNow();
    },
  } as const;

  const bubbleBody = (
    <div
      ref={bubbleRef}
      id={bubbleId}
      role="tooltip"
      data-repancy-tooltip="1"
      data-testid={testId}
      style={{
        position: 'fixed',
        top:
          pos?.placement === 'top'
            ? (pos?.top ?? 0) - 36
            : pos?.top ?? 0,
        left: pos?.left ?? 0,
        maxWidth: MAX_BUBBLE_WIDTH,
        background: '#fffbcc',
        color: '#000',
        border: '1px solid #7f9db9',
        borderRadius: 2,
        padding: '6px 8px',
        fontFamily: FONT,
        fontSize: 11,
        lineHeight: 1.4,
        boxShadow: '2px 2px 0 rgba(0,0,0,0.25)',
        // Pointer-events always 'none'. The bubble is purely informative
        // — never grabs hover. This prevents a re-render flicker loop
        // we previously hit when the bubble overlapped its trigger:
        // hover button → bubble shows → mouse drifts onto bubble →
        // trigger sees pointerleave → bubble hides → mouse is now back
        // on trigger → pointerenter → bubble shows → … The previous
        // "auto when `long` exists, so Learn-more is clickable" tradeoff
        // wasn't worth the thrash. Long bodies should live in a docs
        // route, not a hover tooltip — see /learn/* routes for the
        // canonical place for paragraphs of explainer.
        pointerEvents: 'none',
        zIndex: 99999,
      }}
    >
      <div>{short}</div>
    </div>
  );

  const body = typeof document !== 'undefined' ? document.body : null;
  const portalContent =
    visible && pos !== null && body !== null
      ? createPortal(bubbleBody, body)
      : null;

  if (wrap && isValidElement(props.children)) {
    type ChildProps = {
      ref?: (el: HTMLElement | null) => void;
      onPointerEnter?: (e: PointerEvent) => void;
      onPointerLeave?: (e: PointerEvent) => void;
      onPointerDown?: (e: PointerEvent) => void;
      onPointerUp?: (e: PointerEvent) => void;
      onPointerCancel?: (e: PointerEvent) => void;
      onFocus?: (e: FocusEvent) => void;
      onBlur?: (e: FocusEvent) => void;
      onKeyDown?: (e: KeyboardEvent) => void;
      onClick?: (e: PointerEvent) => void;
      title?: string;
      'aria-describedby'?: string;
      children?: ReactNode;
    };
    const child = props.children as ReactElement<ChildProps>;
    const existing: ChildProps = child.props ?? {};
    const merged: ChildProps = {
      ref: (el: HTMLElement | null) => {
        triggerRef.current = el;
      },
      onPointerEnter: (e) => {
        existing.onPointerEnter?.(e);
        triggerHandlers.onPointerEnter(e);
      },
      onPointerLeave: (e) => {
        existing.onPointerLeave?.(e);
        triggerHandlers.onPointerLeave(e);
      },
      onPointerDown: (e) => {
        existing.onPointerDown?.(e);
        triggerHandlers.onPointerDown(e);
      },
      onPointerUp: (e) => {
        existing.onPointerUp?.(e);
        triggerHandlers.onPointerUp(e);
      },
      onPointerCancel: (e) => {
        existing.onPointerCancel?.(e);
        triggerHandlers.onPointerCancel(e);
      },
      onFocus: (e) => {
        existing.onFocus?.(e);
        triggerHandlers.onFocus(e);
      },
      onBlur: (e) => {
        existing.onBlur?.(e);
        triggerHandlers.onBlur(e);
      },
      onKeyDown: (e) => {
        existing.onKeyDown?.(e);
        triggerHandlers.onKeyDown(e);
      },
      // Suppress native browser tooltip so we don't double-render.
      title: '',
      'aria-describedby': visible ? bubbleId : undefined,
    };
    return (
      <>
        {cloneElement(child, merged)}
        {portalContent}
      </>
    );
  }

  // Standalone (?) chip mode.
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          triggerRef.current = el;
        }}
        aria-label={
          'k' in props && props.k !== undefined
            ? `Help: ${String(props.k)}`
            : `Help: ${short.slice(0, 40)}`
        }
        aria-describedby={visible ? bubbleId : undefined}
        data-help-btn
        data-testid={`${testId}-chip`}
        onPointerEnter={triggerHandlers.onPointerEnter}
        onPointerLeave={triggerHandlers.onPointerLeave}
        onPointerDown={triggerHandlers.onPointerDown}
        onPointerUp={triggerHandlers.onPointerUp}
        onPointerCancel={triggerHandlers.onPointerCancel}
        onFocus={triggerHandlers.onFocus}
        onBlur={triggerHandlers.onBlur}
        onKeyDown={triggerHandlers.onKeyDown}
        onClick={() => {
          if (visible) {
            hideNow();
          } else {
            showNow();
          }
        }}
        style={{
          width: 16,
          height: 16,
          padding: 0,
          background: '#ece9d8',
          border: '1px solid #7f9db9',
          borderRadius: 8,
          fontFamily: FONT,
          fontSize: 10,
          fontWeight: 'bold',
          color: '#1a4ab8',
          cursor: 'help',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
        }}
      >
        ?
      </button>
      {portalContent}
    </>
  );
}
